import { useEffect } from "react";
import { forceAppUpdate } from "@/lib/forceAppUpdate";
import { RefreshCw } from "lucide-react";
import { showUpdateBanner, useUpdateBannerOpen } from "@/hooks/useUpdateBanner";

/**
 * Polls /version.json (no-store). When the server's buildId differs from the
 * id compiled into this running bundle, show a non-blocking "Update available" bar.
 *
 * The banner's own open/closed state now lives in the shared
 * useUpdateBanner store (not local state) so a failed lazy-chunk
 * import elsewhere in the app (see main.tsx's vite:preloadError
 * handler) can show this exact same banner too, instead of forcing an
 * immediate reload that would wipe whatever someone was mid-typing.
 */
export function VersionWatcher() {
  const updateAvailable = useUpdateBannerOpen();

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        if (!data.buildId || cancelled) return;

        // Compared against the id compiled into THIS running bundle, not a stored copy: see
        // vite.config.ts. A page that already is the newest build never prompts.
        if (data.buildId !== __BUILD_ID__) {
          showUpdateBanner();
        }
      } catch {
        /* offline or not yet generated — ignore */
      }
    }

    check();
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    const interval = window.setInterval(check, 60_000);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
      window.clearInterval(interval);
    };
  }, []);

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 z-[100] flex max-w-[min(100vw-2rem,28rem)] -translate-x-1/2 items-center gap-3 rounded-xl border border-primary/30 bg-card px-4 py-3 text-sm shadow-lg"
    >
      <span className="text-foreground">
        A new version of FreeDailyPro is available.
      </span>
      <button
        type="button"
        onClick={() => forceAppUpdate()}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Update
      </button>
    </div>
  );
}
